import * as React from 'react';
import {useEffect, useState} from 'react';
import {Autocomplete} from "@mc/react-autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import CertificateInfo from "../api/sign/types/CertificateInfo";
import ModuleCode from "../api/sign/types/ModuleCode";
import {loadCertificates} from "../action/marbesSignActions";
import * as signActions from "../action/signActions";
import generateRequestId from "../utils/generateRequestId";

type Props = {
    /**
     * Aktuálně vybraný certifikát.
     */
    value?: CertificateInfo;
    /**
     * Změna hodnoty.
     * @param value nová hodnota
     */
    onChange: (value: CertificateInfo) => void;
    /**
     * Callback pro získání seznamu dotupných certifikátů.
     * @param requestId unikátní identifikátor pro načtení certifikátů
     */
    getCerts?: (requestId: string) => Promise<CertificateInfo[]>;
    /**
     * Callback pro načtení aktivním/dostupných modulů pro podepisování.
     */
    getActiveModules?: () => Promise<ModuleCode[]>;
    /**
     * Render certifikátu.
     * @param cert certifikát
     */
    renderCertContent?: (cert?: CertificateInfo) => React.ReactNode;
    /**
     * Automaticky spustit načtení certifikátů.
     */
    autoloading: boolean;
    /**
     * Vypnutí prvku.
     */
    disabled: boolean;
    /**
     * Automaticky vybrat certifikát je-li právě jeden.
     */
    autoselect: boolean;
    /**
     * Automaticky vybrat certifikát je-li právě jeden i v případě, že je vybrán jiný.
     */
    forceAutoselect: boolean;
    /**
     * Callback pro zjištění informací pro probíhajícím načítání.
     * @param loading true - probíhá načítání
     */
    onLoading?: (loading: boolean) => void;
    /**
     * Callback při selhání načtení certifikátů.
     * @param msg   zpráva
     * @param error chyba
     */
    onFailLoading?: (msg: string, error: any) => void;

    /**
     * Vlastní třída.
     */
    className?: string;
}

export const SelectCertificateField = ({
                                           onChange,
                                           onFailLoading,
                                           value,
                                           disabled,
                                           onLoading,
                                           getActiveModules = signActions.getActiveModules,
                                           getCerts = signActions.getCertificates,
                                           autoloading = false,
                                           autoselect = false,
                                           forceAutoselect= false,
                                           renderCertContent = item => item && item.name,
                                           className,
                                       }: Props) => {
    const [certs, setCerts] = useState<CertificateInfo[] | null>(null);
    const [loading, setLoadingState] = useState<boolean>(true);

    const setLoading = (state) => {
        onLoading && onLoading(state);
        setLoadingState(state);
    }

    const load = () => {
        getActiveModules().then((modules) => {
            const requestId = generateRequestId();
            if (modules.indexOf(ModuleCode.MARBES) === -1) { // není třeba používat MC plugin na podepisování
                getCerts(requestId).then((result) => {
                    setLoading(false);
                    setCerts(result);
                }).catch((e) => {
                    setCerts([]);
                    setLoading(false);
                    onFailLoading && onFailLoading("Nepodařilo se získat seznam certifikátů", e);
                });
            } else {
                loadCertificates(requestId).then(() => {
                    getCerts(requestId).then((result) => {
                        setLoading(false);
                        setCerts(result);
                        if (autoselect && value == null && result.length == 1) {
                            onChange && onChange(result[0]);
                        }
                        if (forceAutoselect && result.length == 1) {
                            onChange && onChange(result[0]);
                        }
                    }).catch((e) => {
                        setCerts([]);
                        setLoading(false);
                        onFailLoading && onFailLoading("Nepodařilo se získat seznam certifikátů", e);
                    });
                }).catch(e => {
                    setCerts([]);
                    setLoading(false);
                    onFailLoading && onFailLoading("Nepodařilo se získat seznam certifikátů, pravděpodobně není nainstalován plugin na podepisování", e);
                })
            }
        }).catch((e) => {
            setCerts([]);
            setLoading(false);
            onFailLoading && onFailLoading("Nepodařilo se získat seznam aktivní modulů podepisování", e);
        });
    }

    const handleLoad = () => {
        if (!loading) {
            setLoading(true);
            load();
        }
    }

    const onOpenMenu = () => {
        if (!certs) {
            handleLoad();
        }
    }

    useEffect(() => {
        if (autoloading) {
            load();
        } else {
            setLoading(false);
        }
    }, []);

    return <Autocomplete<CertificateInfo> onOpenMenu={onOpenMenu}
                                          // @ts-ignore
                                          onChange={onChange}
                                          disabled={loading || disabled}
                                          value={value}
                                          actions={certs && [<div
                                              key="reload"
                                              className="btn btn-default mr-2"
                                              onClick={handleLoad}
                                              title="Vynutit přenačtení seznamu certifikátů"
                                          >
                                              <FontAwesomeIcon className="dropdown-button" icon={faSync} />
                                          </div>]}
                                          noItemsFound={loading ? "Načítání seznamu certifikátů..." : "Nebyl dohledán žádný certifikát"}
                                          renderItemContent={renderCertContent}
                                          items={certs || []}
                                          className={className}
    />
};
